/**
 * Extracto por comerciante — para reconciliar e para entregar ao comerciante.
 *
 * Os números vêm do LIVRO, não de uma tabela de resumo. Cada venda mostra o
 * bruto que o cliente pagou, a comissão, o IVA e o líquido que fica para o
 * comerciante — e o extracto assinala qualquer linha em que
 * bruto ≠ líquido + comissão + IVA, que seria defeito.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Grid2 as Grid, CircularProgress, Chip, Divider, Snackbar,
} from '@mui/material';
import { Refresh, ReceiptLong, FileDownload, Warning } from '@mui/icons-material';
import api from '../../../services/api';

const cve = (v: any) => `${Number(v || 0).toLocaleString('pt-PT')}`;

const ExtratoComerciantes: React.FC = () => {
  const [lista, setLista] = useState<any[]>([]);
  const [aCarregar, setACarregar] = useState(true);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [aberto, setAberto] = useState(false);
  const [extracto, setExtracto] = useState<any>(null);
  const [aCarregarExtracto, setACarregarExtracto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setACarregar(true);
    try {
      const p = new URLSearchParams();
      if (de) p.set('de', de);
      if (ate) p.set('ate', ate);
      const r = await api.get(`/admin/merchant-statements?${p}`);
      setLista(r.data.data || []);
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível carregar.');
    } finally {
      setACarregar(false);
    }
  }, [de, ate]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrir = async (entityId: string) => {
    setAberto(true);
    setExtracto(null);
    setACarregarExtracto(true);
    try {
      const p = new URLSearchParams();
      if (de) p.set('de', de);
      if (ate) p.set('ate', ate);
      const r = await api.get(`/admin/merchant-statements/${entityId}?${p}`);
      setExtracto(r.data);
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível carregar o extracto.');
    } finally {
      setACarregarExtracto(false);
    }
  };

  /** Exporta o que está no ecrã, para enviar ao comerciante. */
  const exportar = () => {
    if (!extracto) return;
    const cab = ['Data', 'Tipo', 'Referência', 'Bruto CVE', 'Comissão CVE', 'IVA CVE', 'Líquido CVE'];
    const linhas = extracto.vendas.map((v: any) => [
      new Date(v.data).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }), v.tipo, v.referencia,
      v.brutoCVE, v.comissaoCVE, v.ivaCVE, v.liquidoCVE,
    ]);
    // Ponto e vírgula: o Excel em português trata a vírgula como decimal.
    const csv = [cab, ...linhas].map((l) => l.join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `extracto-${extracto.comerciante.CODIGO || extracto.comerciante.ID}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = (k: string) => lista.reduce((s, l) => s + Number(l[k] || 0), 0);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>Extracto por Comerciante</Typography>
          <Typography variant="body1" color="text.secondary">
            O que cada comerciante vendeu no cashless, o que a Pagali cobrou e o que
            lhe fica. Os números vêm do livro — é contra eles que a transferência bate.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" type="date" label="De" InputLabelProps={{ shrink: true }}
            value={de} onChange={(e) => setDe(e.target.value)} />
          <TextField size="small" type="date" label="Até" InputLabelProps={{ shrink: true }}
            value={ate} onChange={(e) => setAte(e.target.value)} />
          <Button variant="outlined" startIcon={<Refresh />} onClick={carregar}>Actualizar</Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          ['Vendas (bruto)', t('brutoCVE')],
          ['Comissão + IVA', t('comissaoCVE') + t('ivaCVE')],
          ['Líquido aos comerciantes', t('liquidoCVE')],
          ['Por liquidar', t('porLiquidarCVE')],
        ].map(([rot, val]) => (
          <Grid size={{ xs: 6, md: 3 }} key={rot as string}>
            <Card><CardContent>
              <Typography variant="body2" color="text.secondary">{rot}</Typography>
              <Typography variant="h6">{cve(val)} CVE</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      {aCarregar ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : lista.length === 0 ? (
        <Alert severity="info">Nenhum comerciante com movimento no período.</Alert>
      ) : (
        <Card><Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Comerciante</TableCell>
                <TableCell align="right">Transacções</TableCell>
                <TableCell align="right">Bruto</TableCell>
                <TableCell align="right">Comissão</TableCell>
                <TableCell align="right">IVA</TableCell>
                <TableCell align="right">Líquido</TableCell>
                <TableCell align="right">Liquidado</TableCell>
                <TableCell align="right">Por liquidar</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((l) => (
                <TableRow key={l.entityId} hover>
                  <TableCell>
                    <Typography variant="body2">{l.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">{l.codigo}</Typography>
                  </TableCell>
                  <TableCell align="right">{l.transacoes}</TableCell>
                  <TableCell align="right">{cve(l.brutoCVE)}</TableCell>
                  <TableCell align="right">{cve(l.comissaoCVE)}</TableCell>
                  <TableCell align="right">{cve(l.ivaCVE)}</TableCell>
                  <TableCell align="right"><strong>{cve(l.liquidoCVE)}</strong></TableCell>
                  <TableCell align="right">{cve(l.liquidadoCVE)}</TableCell>
                  <TableCell align="right">{cve(l.porLiquidarCVE)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<ReceiptLong />} onClick={() => abrir(l.entityId)}>
                      Detalhe
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box></Card>
      )}

      <Dialog open={aberto} onClose={() => setAberto(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {extracto?.comerciante?.DESIG || 'Extracto'}
          {extracto && (
            <Button size="small" startIcon={<FileDownload />} onClick={exportar}>Exportar CSV</Button>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {aCarregarExtracto || !extracto ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
          ) : (
            <>
              {extracto.totais.naoConferem > 0 && (
                <Alert severity="error" icon={<Warning />} sx={{ mb: 2 }}>
                  {extracto.totais.naoConferem} transacção(ões) em que o bruto não é igual à
                  soma do líquido, comissão e IVA. O extracto não pode ser entregue assim.
                </Alert>
              )}

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                  ['Transacções', extracto.totais.transacoes],
                  ['Bruto', `${cve(extracto.totais.brutoCVE)} CVE`],
                  ['Comissão', `${cve(extracto.totais.comissaoCVE)} CVE`],
                  ['IVA', `${cve(extracto.totais.ivaCVE)} CVE`],
                  ['Líquido', `${cve(extracto.totais.liquidoCVE)} CVE`],
                  ['Por liquidar', `${cve(extracto.totais.porLiquidarCVE)} CVE`],
                ].map(([rot, val]) => (
                  <Grid size={{ xs: 6, sm: 4, md: 2 }} key={rot as string}>
                    <Typography variant="caption" color="text.secondary">{rot}</Typography>
                    <Typography variant="subtitle1">{val as any}</Typography>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Vendas</Typography>
              <Box sx={{ overflowX: 'auto', mb: 3 }}>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Data</TableCell><TableCell>Tipo</TableCell>
                    <TableCell align="right">Bruto</TableCell><TableCell align="right">Comissão</TableCell>
                    <TableCell align="right">IVA</TableCell><TableCell align="right">Líquido</TableCell>
                    <TableCell>Referência</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {extracto.vendas.map((v: any, i: number) => (
                      <TableRow key={i} sx={!v.confere ? { bgcolor: 'error.light' } : undefined}>
                        <TableCell>{new Date(v.data).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}</TableCell>
                        <TableCell><Chip size="small" variant="outlined" label={v.tipo} /></TableCell>
                        <TableCell align="right">{cve(v.brutoCVE)}</TableCell>
                        <TableCell align="right">{cve(v.comissaoCVE)}</TableCell>
                        <TableCell align="right">{cve(v.ivaCVE)}</TableCell>
                        <TableCell align="right"><strong>{cve(v.liquidoCVE)}</strong></TableCell>
                        <TableCell><Typography variant="caption">{v.referencia}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Typography variant="subtitle2" gutterBottom>Transferências feitas</Typography>
              {extracto.liquidacoes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhuma no período.</Typography>
              ) : (
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Data</TableCell><TableCell align="right">Valor</TableCell>
                    <TableCell>Referência</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {extracto.liquidacoes.map((l: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{new Date(l.data).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}</TableCell>
                        <TableCell align="right">{cve(l.valorCVE)}</TableCell>
                        <TableCell><Typography variant="caption">{l.referencia}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setAberto(false)}>Fechar</Button></DialogActions>
      </Dialog>

      <Snackbar open={Boolean(erro)} autoHideDuration={6000} onClose={() => setErro(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="error" variant="filled" onClose={() => setErro(null)}>{erro}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ExtratoComerciantes;
